// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { formatCount, pluralize } = require('shared/string-utils')
// Common client-V2 imports
const tag = require('client-v2/common/utils/hyperscript')
const CirclePositioner = require('client-v2/common/components/circle-positioner')
const { CollectionSubscribeCircleButton } = require('client-v2/common/components/buttons')
const LazyImage = require('client-v2/common/components/lazy-image')
const MatchMedia = require('client-v2/common/components/match-media')
const { registerComponentEventHandlers } = require('client-v2/common/components/utils')
const {
  collectionProfileHref,
  collectionProfileSubscribersHref,
  collectionUserProfileHref
} = require('client-v2/common/models/collection/actions')
const CollectionModelRecord = require('client-v2/common/models/collection/record')
const PageContextRecord = require('client-v2/common/models/page-context/record')
const { mediaQueries } = require('client-v2/common/utils/responsive')

const bem = BemEntity('CollectionCarouselSlide')
const Subscribers = pluralize('Subscriber')

class CollectionCarouselSlide extends React.PureComponent {
  constructor(props) {
    super(props)
    // Ensure function references do not change between render calls
    registerComponentEventHandlers(this)
  }

  collectionHref() {
    if (this.props.isHrefsEnabled) {
      return collectionProfileHref(this.props.collection)
    }
  }

  collectionSubscribersHref() {
    if (this.props.isHrefsEnabled) {
      return collectionProfileSubscribersHref(this.props.collection)
    }
  }

  userHref() {
    if (this.props.isHrefsEnabled) {
      return collectionUserProfileHref(this.props.collection)
    }
  }

  renderByline() {
    return (
      tag('div', {
        className: bem.element('byline')
      },
        `by `,
        tag('a', {
          className: bem.element('byline-href'),
          href: this.userHref(),
          onClick: this.onUsernameClick
        },
          `@${this.props.collection.username}`
        )
      )
    )
  }

  renderCaption(label) {
    return (
      tag('div', {
        className: bem.element('caption')
      },
        label
      )
    )
  }

  renderDescription() {
    return (
      tag('p', {
        className: bem.element('description'),
        onClick: this.onDescriptionClick
      },
        this.props.collection.description
      )
    )
  }

  renderDetails() {
    return (
      tag('div', {
        className: bem.element('details-container')
      },
        this.renderCaption('Featured Collection'),
        this.renderTitle(),
        this.renderByline(),
        this.renderDescription(),
        this.renderViewButton()
      )
    )
  }

  renderImage(hasOverlay = false) {
    const { collection: { images } } = this.props
    return (
      tag('div', {
        className: bem.element('image-container'),
        onClick: this.onImageClick
      },
        tag(LazyImage, {
          className: bem.element('image'),
          sources: images
        }),
        (
          hasOverlay
          ? this.renderImageOverlay()
          : null
        ),
        this.renderPositionedCircleContent()
      )
    )
  }

  renderImageOverlay() {
    return (
      tag('div', {
        className: bem.element('image-overlay')
      },
        this.renderCaption('Featured'),
        this.renderTitle(),
        this.renderByline()
      )
    )
  }

  renderPositionedCircleContent() {
    if (this.props.isSelf || !this.props.onFollowClick) {
      return null
    }
    return (
      tag(CirclePositioner, {
        className: bem.element('positioned')
      },
        this.renderSubscribeButton(),
        this.renderSubscribersCount()
      )
    )
  }

  renderSubscribeButton() {
    return tag(CollectionSubscribeCircleButton, {
      className: bem.element('subscribe-button'),
      checked: this.props.collection.isFollowing,
      onClick: this.onFollowClick
    })
  }

  renderSubscribersCount() {
    const count = this.props.collection['follower_count']
    if (!this.props.hasSubscribersCount || !count) {
      return null
    }
    return tag('a', {
      className: bem.element('subscribers-count'),
      href: this.collectionSubscribersHref(),
      title: `This Collection has ${count} ${Subscribers(count)}`,
      onClick: this.onCountClick
    },
      `${formatCount(count)}`
    )
  }

  renderTitle() {
    return (
      tag('h2', {
        className: bem.element('title'),
        onClick: this.onTitleClick
      },
        this.props.collection.name
      )
    )
  }

  renderViewButton() {
    return (
      tag('button', {
        className: bem.element('view-button'),
        onClick: this.onViewClick
      },
        `View collection`
      )
    )
  }

  renderInnerColumn() {
    return (
      tag('div', {
        className: bem.element('inner-column')
      },
        this.renderImage(true),
        this.renderDescription(),
        this.renderViewButton()
      )
    )
  }

  renderInnerRow() {
    return (
      tag('div', {
        className: bem.element('inner-row')
      },
        tag('div', {
          className: bem.element('inner-section', 'details')
        },
          this.renderDetails()
        ),
        tag('div', {
          className: bem.element('inner-section', 'image')
        },
          this.renderImage(false)
        )
      )
    )
  }

  render() {
    return (
      tag('div', {
        className: classNames(
          this.props.className,
          bem.block()
        )
      },
        tag(MatchMedia, {
          query: mediaQueries['medium-and-down']
        },
          this.renderInnerColumn()
        ),
        tag(MatchMedia, {
          query: mediaQueries['large-and-up']
        },
          this.renderInnerRow()
        )
      )
    )
  }
}

CollectionCarouselSlide.propTypes = {
  // Flags
  hasSubscribersCount: PropTypes.bool,
  isHrefsEnabled: PropTypes.bool,
  isSelf: PropTypes.bool,
  // Values
  className: PropTypes.string,
  collection: PropTypes.shape(CollectionModelRecord.propTypes),
  pageContext: PropTypes.shape(PageContextRecord.propTypes),
  // Callbacks
  onFollowClick: PropTypes.func
}

// Callbacks for event handlers
CollectionCarouselSlide.eventHandlerKeys = [
  'onCountClick',
  'onDescriptionClick',
  'onFollowClick',
  'onImageClick',
  'onTitleClick',
  'onUsernameClick',
  'onViewClick'
]
CollectionCarouselSlide.eventHandlerKeys.forEach(key => {
  CollectionCarouselSlide.propTypes[key] = PropTypes.func
})

CollectionCarouselSlide.defaultProps = {
  hasSubscribersCount: true,
  isHrefsEnabled: true
}

module.exports = CollectionCarouselSlide
